// src/pages/StaffDirectory.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, CircularProgress, Select, MenuItem, FormControl,
  InputLabel, Chip, Card, CardContent, Avatar, useTheme, Alert, Grid,
  InputAdornment, Tooltip // ДОДАНО Tooltip
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Person as PersonIcon,
  Groups as GroupsIcon, Engineering as TechIcon, AdminPanelSettings as AdminIcon,
  Visibility as SupervisorIcon, Search, FilterList, Visibility as VisibilityIcon // ДОДАНО VisibilityIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

import { getData, saveData } from '../services/storageService';
import { useNotifications } from '../context/NotificationContext';
import { useI18n } from '../i18n/i18n';

const DEPARTMENTS = [
  'General', 'SECADOR/A Y/O CARGA TÚNEL', 'CONTROL DE TÚNEL', 
  'CHÓFER', 'Mantenimiento', 'Administración'
];

const ROLES = ['operator', 'technician', 'supervisor', 'admin'];
const STATUSES = ['active', 'inactive'];

const StaffDirectoryPage = () => {
  const theme = useTheme();
  const { showNotification } = useNotifications();
  const { t } = useI18n();
  
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [staffToDelete, setStaffToDelete] = useState(null);

  // НОВІ СТАНИ ДЛЯ ФІЛЬТРІВ ТА ПОШУКУ
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    const loadStaff = async () => {
      setLoading(true);
      try {
        const data = await getData('staff') || [];
        setStaff(data);
      } catch (error) {
        console.error("Error loading staff:", error);
        showNotification(t('staff_directory.load_error'), 'error');
      } finally {
        setLoading(false);
      }
    };
    loadStaff();
  }, [showNotification, t]);

  // ФІЛЬТРАЦІЯ ТА СОРТУВАННЯ
  const filteredStaff = useMemo(() => {
    return staff
      .filter(employee => {
        // Пошук по імені, email, відділу, посаді
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          (employee.name && employee.name.toLowerCase().includes(searchLower)) ||
          (employee.firstName && employee.firstName.toLowerCase().includes(searchLower)) ||
          (employee.lastName && employee.lastName.toLowerCase().includes(searchLower)) ||
          (employee.email && employee.email.toLowerCase().includes(searchLower)) ||
          (employee.department && employee.department.toLowerCase().includes(searchLower)) ||
          (employee.position && employee.position.toLowerCase().includes(searchLower));

        // Фільтри
        const matchesDepartment = departmentFilter === 'all' || employee.department === departmentFilter;
        const matchesRole = roleFilter === 'all' || employee.role === roleFilter;
        const matchesStatus = statusFilter === 'all' || employee.status === statusFilter;

        return matchesSearch && matchesDepartment && matchesRole && matchesStatus;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'name':
            const nameA = a.name || `${a.firstName} ${a.lastName}`;
            const nameB = b.name || `${b.firstName} ${b.lastName}`;
            return nameA.localeCompare(nameB);
          
          case 'department':
            return (a.department || '').localeCompare(b.department || '');
          
          case 'role':
            return (a.role || '').localeCompare(b.role || '');
          
          case 'status':
            return (a.status || '').localeCompare(b.status || '');
          
          default:
            return 0;
        }
      });
  }, [staff, searchTerm, departmentFilter, roleFilter, statusFilter, sortBy]);

  // СТАТИСТИКА ПО ФІЛЬТРАМ
  const filterStats = useMemo(() => {
    const total = staff.length;
    const filtered = filteredStaff.length;
    return { total, filtered, hidden: total - filtered };
  }, [staff.length, filteredStaff.length]);

  // ФУНКЦІЇ ДЛЯ РОБОТИ З ДАНИМИ
  const handleOpenDialog = (item = null) => {
    setCurrentItem(item || { 
      id: null, name: '', department: 'General', position: '', 
      status: 'active', role: 'operator', email: '', phone: ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  const handleSave = async () => {
    if (!currentItem || !currentItem.name.trim()) {
      showNotification(t('staff_directory.name_required'), 'warning');
      return;
    }

    let updatedStaff = [...staff];
    
    if (currentItem.id) {
      updatedStaff = staff.map(item => 
        item.id === currentItem.id ? currentItem : item
      );
    } else {
      updatedStaff.push({ 
        ...currentItem, 
        id: Date.now(),
        createdAt: new Date().toISOString()
      });
    }
    
    try {
      await saveData('staff', updatedStaff);
      setStaff(updatedStaff);
      handleCloseDialog();
      showNotification(
        currentItem.id ? t('staff_directory.update_success') : t('staff_directory.add_success'), 
        'success'
      );
    } catch (error) {
      console.error("Error saving staff:", error);
      showNotification(t('staff_directory.save_error'), 'error');
    }
  };

  // ВИПРАВЛЕНА ФУНКЦІЯ - ДОДАНО openDeleteDialog
  const openDeleteDialog = (staffItem) => {
    setStaffToDelete(staffItem);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setStaffToDelete(null);
  };

  const handleDelete = async () => {
    if (!staffToDelete) return;
    
    try {
      const updatedStaff = staff.filter(item => item.id !== staffToDelete.id);
      await saveData('staff', updatedStaff);
      setStaff(updatedStaff);
      showNotification(t('staff_directory.delete_success'), 'success');
    } catch (error) {
      console.error("Error deleting staff:", error);
      showNotification(t('staff_directory.delete_error'), 'error');
    } finally {
      closeDeleteDialog();
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentItem(prev => ({ ...prev, [name]: value }));
  };

  // Допоміжні функції для іконок ролей
  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <AdminIcon />;
      case 'technician': return <TechIcon />;
      case 'supervisor': return <SupervisorIcon />;
      default: return <PersonIcon />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'technician': return 'warning';
      case 'supervisor': return 'info';
      default: return 'primary';
    }
  };

  // Статистика
  const stats = {
    total: staff.length,
    active: staff.filter(s => s.status === 'active').length,
    byDepartment: DEPARTMENTS.reduce((acc, dept) => {
      acc[dept] = staff.filter(s => s.department === dept).length;
      return acc;
    }, {})
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Заголовок */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Avatar sx={{ width: 80, height: 80, bgcolor: theme.palette.primary.main, mx: 'auto', mb: 2 }}>
          <GroupsIcon sx={{ fontSize: 40 }} />
        </Avatar>
        <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {t('staff_directory.title')}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {t('staff_directory.subtitle')}
        </Typography>
      </Box>

      {/* НОВА ПАНЕЛЬ ПОШУКУ ТА ФІЛЬТРІВ */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Пошук */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder={t('staff_directory.search_placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Фільтр відділу */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('staff_directory.department')}</InputLabel>
                <Select
                  value={departmentFilter}
                  label={t('staff_directory.department')}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <MenuItem value="all">{t('staff_directory.all_departments')}</MenuItem>
                  {DEPARTMENTS.map(dept => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Фільтр ролі */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('staff_directory.role')}</InputLabel>
                <Select
                  value={roleFilter}
                  label={t('staff_directory.role')}
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <MenuItem value="all">{t('staff_directory.all_roles')}</MenuItem>
                  {ROLES.map(role => (
                    <MenuItem key={role} value={role}>
                      {t(`roles.${role}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Фільтр статусу */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('staff_directory.status')}</InputLabel>
                <Select
                  value={statusFilter}
                  label={t('staff_directory.status')}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">{t('staff_directory.all_statuses')}</MenuItem>
                  {STATUSES.map(status => (
                    <MenuItem key={status} value={status}>
                      {t(`staff_directory.${status}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Сортування */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('staff_directory.sort_by')}</InputLabel>
                <Select
                  value={sortBy}
                  label={t('staff_directory.sort_by')}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <MenuItem value="name">{t('staff_directory.sort_name')}</MenuItem>
                  <MenuItem value="department">{t('staff_directory.sort_department')}</MenuItem>
                  <MenuItem value="role">{t('staff_directory.sort_role')}</MenuItem>
                  <MenuItem value="status">{t('staff_directory.sort_status')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Статистика фільтрів */}
          {filterStats.hidden > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('staff_directory.filter_stats', {
                filtered: filterStats.filtered,
                total: filterStats.total,
                hidden: filterStats.hidden
              })}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, textAlign: 'center' }}>
            <CardContent>
              <Typography variant="h3" color="primary" sx={{ fontWeight: 700 }}>
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('staff_directory.total_employees')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, textAlign: 'center' }}>
            <CardContent>
              <Typography variant="h3" color="success.main" sx={{ fontWeight: 700 }}>
                {stats.active}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('staff_directory.active_employees')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, textAlign: 'center' }}>
            <CardContent>
              <Typography variant="h3" color="info.main" sx={{ fontWeight: 700 }}>
                {staff.filter(s => s.role === 'operator').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('staff_directory.operators')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, textAlign: 'center' }}>
            <CardContent>
              <Typography variant="h3" color="warning.main" sx={{ fontWeight: 700 }}>
                {staff.filter(s => s.role === 'technician').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('staff_directory.technicians')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Основна картка з таблицею */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="primary" />
              {t('staff_directory.employee_list')} ({filteredStaff.length})
              {filterStats.hidden > 0 && (
                <Chip 
                  label={`-${filterStats.hidden}`}
                  size="small"
                  color="default"
                  variant="outlined"
                />
              )}
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={() => handleOpenDialog()}
              sx={{ borderRadius: 2 }}
            >
              {t('staff_directory.add_employee')}
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{t('staff_directory.employee')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{t('staff_directory.department')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{t('staff_directory.position')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{t('staff_directory.role')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{t('staff_directory.status')}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>{t('staff_directory.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredStaff.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 40, height: 40, bgcolor: theme.palette.primary.light }}>
                          {getRoleIcon(item.role)}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {item.name || `${item.firstName} ${item.lastName}`}
                          </Typography>
                          {item.email && (
                            <Typography variant="caption" color="text.secondary">
                              {item.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.department}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.position || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        icon={getRoleIcon(item.role)}
                        label={t(`roles.${item.role}`)}
                        color={getRoleColor(item.role)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.status === 'active' ? t('staff_directory.active') : t('staff_directory.inactive')}
                        color={item.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title={t('staff_directory.view_profile')}>
                          <IconButton 
                            component={Link}
                            to={`/employee/${item.id}`}
                            color="info"
                            size="small"
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.edit')}>
                          <IconButton 
                            onClick={() => handleOpenDialog(item)} 
                            color="primary"
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.delete')}>
                          <IconButton 
                            onClick={() => openDeleteDialog(item)} 
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredStaff.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        {searchTerm || departmentFilter !== 'all' || roleFilter !== 'all' || statusFilter !== 'all' 
                          ? t('staff_directory.no_results')
                          : t('staff_directory.no_employees')
                        }
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {searchTerm || departmentFilter !== 'all' || roleFilter !== 'all' || statusFilter !== 'all'
                          ? t('staff_directory.try_adjusting_filters')
                          : t('staff_directory.add_first_employee')
                        }
                      </Typography>
                      {(searchTerm || departmentFilter !== 'all' || roleFilter !== 'all' || statusFilter !== 'all') && (
                        <Button 
                          variant="outlined"
                          onClick={() => {
                            setSearchTerm('');
                            setDepartmentFilter('all');
                            setRoleFilter('all');
                            setStatusFilter('all');
                          }}
                          sx={{ mr: 2 }}
                        >
                          {t('staff_directory.clear_filters')}
                        </Button>
                      )}
                      <Button 
                        variant="contained" 
                        startIcon={<AddIcon />} 
                        onClick={() => handleOpenDialog()}
                        sx={{ borderRadius: 2 }}
                      >
                        {t('staff_directory.add_first_employee_btn')}
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Діалог редагування */}
      <EditEmployeeDialog 
        open={openDialog}
        onClose={handleCloseDialog}
        employee={currentItem}
        onSave={handleSave}
        onInputChange={handleInputChange}
        t={t}
      />

      {/* Діалог підтвердження видалення */}
      <DeleteConfirmationDialog 
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={handleDelete}
        employee={staffToDelete}
        t={t}
      />
    </Box>
  );
};

// Компонент діалогу редагування
const EditEmployeeDialog = ({ open, onClose, employee, onSave, onInputChange, t }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {employee?.id ? t('staff_directory.edit_employee') : t('staff_directory.add_employee')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('staff_directory.first_name')}
              name="firstName"
              value={employee?.firstName || ''}
              onChange={onInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('staff_directory.last_name')}
              name="lastName"
              value={employee?.lastName || ''}
              onChange={onInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{t('staff_directory.department')}</InputLabel>
              <Select
                name="department"
                value={employee?.department || ''}
                label={t('staff_directory.department')}
                onChange={onInputChange}
              >
                {DEPARTMENTS.map(dep => <MenuItem key={dep} value={dep}>{dep}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('staff_directory.position')}
              name="position"
              value={employee?.position || ''}
              onChange={onInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{t('staff_directory.role')}</InputLabel>
              <Select
                name="role"
                value={employee?.role || ''}
                label={t('staff_directory.role')}
                onChange={onInputChange}
              >
                <MenuItem value="operator">{t('roles.operator')}</MenuItem>
                <MenuItem value="technician">{t('roles.technician')}</MenuItem>
                <MenuItem value="supervisor">{t('roles.supervisor')}</MenuItem>
                <MenuItem value="admin">{t('roles.admin')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>{t('staff_directory.status')}</InputLabel>
              <Select
                name="status"
                value={employee?.status || 'active'}
                label={t('staff_directory.status')}
                onChange={onInputChange}
              >
                <MenuItem value="active">{t('staff_directory.active')}</MenuItem>
                <MenuItem value="inactive">{t('staff_directory.inactive')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              name="email"
              value={employee?.email || ''}
              onChange={onInputChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t('staff_directory.phone')}
              type="tel"
              name="phone"
              value={employee?.phone || ''}
              onChange={onInputChange}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={onSave} variant="contained">
          {employee?.id ? t('common.update') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Компонент діалогу підтвердження видалення
const DeleteConfirmationDialog = ({ open, onClose, onConfirm, employee, t }) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {t('staff_directory.confirm_delete')}
      </DialogTitle>
      <DialogContent>
        <Typography>
          {t('staff_directory.delete_confirmation', { name: employee?.name || `${employee?.firstName} ${employee?.lastName}` })}
        </Typography>
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t('staff_directory.delete_warning')}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={onConfirm} variant="contained" color="error">
          {t('staff_directory.delete_employee')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StaffDirectoryPage;